import React, { useEffect, useRef, useState } from 'react';
import { shopApi } from '../utils/shopApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Image as ImageIcon, Upload, Sparkles, Trash2, Save, Search, Plus, RefreshCw } from 'lucide-react';

const formatRWF = (n) => new Intl.NumberFormat('en-RW', { maximumFractionDigits: 0 }).format(n);

const BLANK_PRODUCT = {
  name: '',
  category: '',
  unit: 'kg',
  base_price: 0,
  cost_price: 0,
  current_stock: 0,
  low_stock_threshold: 0,
  online_price: 0,
  online_discount_percent: 0,
  is_visible_online: true,
  short_description: '',
  sku: '',
  barcode: '',
};

export default function ShopProductsPage() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);        // existing product being edited (has id)
  const [creating, setCreating] = useState(null);      // new product form (no id)
  const [imagePreview, setImagePreview] = useState(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncCandidates, setSyncCandidates] = useState([]);
  const [syncSelected, setSyncSelected] = useState(new Set());
  const [syncLoading, setSyncLoading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    shopApi.get('/shop/admin/products').then((r) => setProducts(r.data));

  useEffect(() => {
    load();
  }, []);

  const openEditor = async (p) => {
    setEditing({
      ...p,
      base_price: p.base_price ?? 0,
      cost_price: p.cost_price ?? 0,
      current_stock: p.current_stock ?? 0,
      low_stock_threshold: p.low_stock_threshold ?? 0,
      online_price: p.online_price ?? 0,
      online_discount_percent: p.online_discount_percent ?? 0,
      is_visible_online: p.is_visible_online !== false,
      short_description: p.short_description || '',
      sku: p.sku || '',
      barcode: p.barcode || '',
    });
    if (p.has_image) {
      try {
        const r = await shopApi.get(`/shop/admin/products/${p.id}/image`);
        setImagePreview(r.data.image_url);
      } catch {
        setImagePreview(null);
      }
    } else {
      setImagePreview(null);
    }
  };

  const closeEditor = () => {
    setEditing(null);
    setImagePreview(null);
  };
  const closeCreator = () => setCreating(null);

  const saveExisting = async () => {
    try {
      await shopApi.put(`/shop/admin/products/${editing.id}`, {
        name: editing.name,
        category: editing.category,
        unit: editing.unit || 'kg',
        base_price: parseFloat(editing.base_price) || 0,
        cost_price: parseFloat(editing.cost_price) || 0,
        current_stock: parseFloat(editing.current_stock) || 0,
        low_stock_threshold: parseFloat(editing.low_stock_threshold) || 0,
        online_price: parseFloat(editing.online_price) || 0,
        online_discount_percent: parseFloat(editing.online_discount_percent) || 0,
        is_visible_online: editing.is_visible_online,
        short_description: editing.short_description,
        sku: editing.sku,
        barcode: editing.barcode,
      });
      toast.success('Product updated');
      closeEditor();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
  };

  const createProduct = async () => {
    if (!creating.name || !creating.category) {
      toast.error('Name and category are required');
      return;
    }
    try {
      await shopApi.post('/shop/admin/products', {
        name: creating.name,
        category: creating.category,
        unit: creating.unit || 'kg',
        base_price: parseFloat(creating.base_price) || 0,
        cost_price: parseFloat(creating.cost_price) || 0,
        current_stock: parseFloat(creating.current_stock) || 0,
        low_stock_threshold: parseFloat(creating.low_stock_threshold) || 0,
        online_price: parseFloat(creating.online_price) || 0,
        online_discount_percent: parseFloat(creating.online_discount_percent) || 0,
        is_visible_online: creating.is_visible_online,
        short_description: creating.short_description,
        sku: creating.sku,
        barcode: creating.barcode,
      });
      toast.success('Product created');
      closeCreator();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create');
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from the shop catalog? This cannot be undone.`)) return;
    try {
      await shopApi.delete(`/shop/admin/products/${p.id}`);
      toast.success('Product deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      toast.info('Uploading image…');
      await shopApi.post(`/shop/admin/products/${editing.id}/upload-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Image uploaded');
      const r = await shopApi.get(`/shop/admin/products/${editing.id}/image`);
      setImagePreview(r.data.image_url);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
  };

  const generateImage = async () => {
    try {
      toast.info('Generating image with AI… this can take ~20s');
      await shopApi.post(`/shop/admin/products/${editing.id}/generate-image`);
      toast.success('Image generated');
      const r = await shopApi.get(`/shop/admin/products/${editing.id}/image`);
      setImagePreview(r.data.image_url);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Generation failed');
    }
  };

  const removeImage = async () => {
    if (!window.confirm('Remove this product image?')) return;
    await shopApi.delete(`/shop/admin/products/${editing.id}/image`);
    setImagePreview(null);
    toast.success('Image removed');
    load();
  };

  const openSync = async () => {
    setSyncOpen(true);
    setSyncLoading(true);
    setSyncSelected(new Set());
    try {
      const r = await shopApi.get('/shop/admin/products/sync-candidates');
      setSyncCandidates(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load candidates');
    } finally {
      setSyncLoading(false);
    }
  };

  const toggleSyncItem = (id) => {
    setSyncSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSyncAll = () => {
    setSyncSelected((prev) =>
      prev.size === syncCandidates.length ? new Set() : new Set(syncCandidates.map((c) => c.id))
    );
  };

  const runSync = async () => {
    if (syncSelected.size === 0) {
      toast.error('Select at least one product');
      return;
    }
    try {
      const r = await shopApi.post('/shop/admin/products/sync', {
        product_ids: Array.from(syncSelected),
      });
      const inserted = r.data?.inserted || 0;
      const skipped = r.data?.skipped || [];
      toast.success(
        `Synced ${inserted} product${inserted === 1 ? '' : 's'}` +
          (skipped.length ? ` (${skipped.length} skipped as duplicates)` : '')
      );
      setSyncOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    }
  };

  const filtered = products.filter(
    (p) => !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-serif font-bold">Shop Inventory</h1>
          <p className="text-sm text-muted-foreground">
            Independent catalog for the online store — separate from the management inventory.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={openSync}
            data-testid="shop-sync-from-mgmt-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Sync from Management
          </Button>
          <Button
            onClick={() => setCreating({ ...BLANK_PRODUCT })}
            data-testid="shop-product-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Product
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search products…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="shop-products-search"
        />
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3">Image</th>
              <th className="text-left p-3">Product</th>
              <th className="text-left p-3">Category</th>
              <th className="text-right p-3">Stock</th>
              <th className="text-right p-3">Base price</th>
              <th className="text-right p-3">Online price</th>
              <th className="text-center p-3">Visible</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-muted-foreground text-sm">
                  No products yet. Click <b>Add Product</b> to build your online catalog.
                </td>
              </tr>
            )}
            {filtered.map((p) => (
              <tr
                key={p.id}
                className="border-t border-border hover:bg-muted/20"
                data-testid={`shop-product-row-${p.id}`}
              >
                <td className="p-2">
                  <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center">
                    {p.has_image ? (
                      <ImageIcon className="w-4 h-4 text-green-600" />
                    ) : (
                      <ImageIcon className="w-4 h-4 text-muted-foreground/40" />
                    )}
                  </div>
                </td>
                <td className="p-3 font-medium">{p.name}</td>
                <td className="p-3 text-muted-foreground capitalize">{p.category}</td>
                <td className="p-3 text-right">
                  <span
                    className={
                      (p.current_stock ?? 0) <= (p.low_stock_threshold ?? 0)
                        ? 'text-red-600 font-semibold'
                        : ''
                    }
                  >
                    {formatRWF(p.current_stock || 0)} {p.unit}
                  </span>
                </td>
                <td className="p-3 text-right text-muted-foreground">
                  RWF {formatRWF(p.base_price || 0)}
                </td>
                <td className="p-3 text-right font-semibold">
                  RWF {formatRWF(p.online_price || 0)}{' '}
                  {p.online_discount_percent > 0 && (
                    <span className="text-xs text-green-600 ml-1">-{p.online_discount_percent}%</span>
                  )}
                </td>
                <td className="p-3 text-center">
                  {p.is_visible_online ? (
                    <span className="text-green-600 text-xs">✓</span>
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditor(p)}
                    data-testid={`shop-product-edit-${p.id}`}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteProduct(p)}
                    data-testid={`shop-product-delete-${p.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && closeEditor()}>
        {editing && (
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.name || 'Edit product'}</DialogTitle>
            </DialogHeader>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Product image</Label>
                <div className="mt-1 aspect-square rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt={editing.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="w-12 h-12 text-muted-foreground/50" />
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}
                  data-testid="shop-image-file"
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    data-testid="shop-image-upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Upload
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={generateImage}
                    data-testid="shop-image-generate-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Generate with AI
                  </Button>
                  {imagePreview && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={removeImage}
                      data-testid="shop-image-remove-btn"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={editing.name}
                    onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                    data-testid="shop-edit-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Category</Label>
                    <Input
                      value={editing.category}
                      onChange={(e) => setEditing((s) => ({ ...s, category: e.target.value }))}
                      data-testid="shop-edit-category"
                    />
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Input
                      value={editing.unit}
                      onChange={(e) => setEditing((s) => ({ ...s, unit: e.target.value }))}
                      data-testid="shop-edit-unit"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Base price (RWF)</Label>
                    <Input
                      type="number"
                      value={editing.base_price}
                      onChange={(e) => setEditing((s) => ({ ...s, base_price: e.target.value }))}
                      data-testid="shop-edit-base-price"
                    />
                  </div>
                  <div>
                    <Label>Cost price (RWF)</Label>
                    <Input
                      type="number"
                      value={editing.cost_price}
                      onChange={(e) => setEditing((s) => ({ ...s, cost_price: e.target.value }))}
                      data-testid="shop-edit-cost-price"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Current stock</Label>
                    <Input
                      type="number"
                      value={editing.current_stock}
                      onChange={(e) => setEditing((s) => ({ ...s, current_stock: e.target.value }))}
                      data-testid="shop-edit-stock"
                    />
                  </div>
                  <div>
                    <Label>Low-stock alert at</Label>
                    <Input
                      type="number"
                      value={editing.low_stock_threshold}
                      onChange={(e) =>
                        setEditing((s) => ({ ...s, low_stock_threshold: e.target.value }))
                      }
                      data-testid="shop-edit-low-stock"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Online price (RWF)</Label>
                    <Input
                      type="number"
                      value={editing.online_price}
                      onChange={(e) => setEditing((s) => ({ ...s, online_price: e.target.value }))}
                      data-testid="shop-online-price"
                    />
                  </div>
                  <div>
                    <Label>Discount (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="90"
                      value={editing.online_discount_percent}
                      onChange={(e) =>
                        setEditing((s) => ({ ...s, online_discount_percent: e.target.value }))
                      }
                      data-testid="shop-online-discount"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>SKU (optional)</Label>
                    <Input
                      value={editing.sku}
                      onChange={(e) => setEditing((s) => ({ ...s, sku: e.target.value }))}
                      data-testid="shop-edit-sku"
                    />
                  </div>
                  <div>
                    <Label>Barcode (optional)</Label>
                    <Input
                      value={editing.barcode}
                      onChange={(e) => setEditing((s) => ({ ...s, barcode: e.target.value }))}
                      data-testid="shop-edit-barcode"
                    />
                  </div>
                </div>
                <div>
                  <Label>Short description (optional)</Label>
                  <Input
                    value={editing.short_description}
                    onChange={(e) =>
                      setEditing((s) => ({ ...s, short_description: e.target.value }))
                    }
                    placeholder="e.g. Locally grown, organic"
                    data-testid="shop-desc"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editing.is_visible_online}
                    onCheckedChange={(v) =>
                      setEditing((s) => ({ ...s, is_visible_online: v }))
                    }
                    data-testid="shop-visible-switch"
                  />
                  <Label>Visible on storefront</Label>
                </div>
                <Button
                  onClick={saveExisting}
                  className="w-full"
                  data-testid="shop-product-save"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* Create dialog */}
      <Dialog open={!!creating} onOpenChange={(open) => !open && closeCreator()}>
        {creating && (
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add shop product</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Name *</Label>
                <Input
                  value={creating.name}
                  onChange={(e) => setCreating((s) => ({ ...s, name: e.target.value }))}
                  data-testid="shop-create-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Category *</Label>
                  <Input
                    value={creating.category}
                    onChange={(e) => setCreating((s) => ({ ...s, category: e.target.value }))}
                    data-testid="shop-create-category"
                  />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Input
                    value={creating.unit}
                    onChange={(e) => setCreating((s) => ({ ...s, unit: e.target.value }))}
                    data-testid="shop-create-unit"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Base price (RWF)</Label>
                  <Input
                    type="number"
                    value={creating.base_price}
                    onChange={(e) => setCreating((s) => ({ ...s, base_price: e.target.value }))}
                    data-testid="shop-create-base-price"
                  />
                </div>
                <div>
                  <Label>Cost price (RWF)</Label>
                  <Input
                    type="number"
                    value={creating.cost_price}
                    onChange={(e) => setCreating((s) => ({ ...s, cost_price: e.target.value }))}
                    data-testid="shop-create-cost-price"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Initial stock</Label>
                  <Input
                    type="number"
                    value={creating.current_stock}
                    onChange={(e) => setCreating((s) => ({ ...s, current_stock: e.target.value }))}
                    data-testid="shop-create-stock"
                  />
                </div>
                <div>
                  <Label>Low-stock alert at</Label>
                  <Input
                    type="number"
                    value={creating.low_stock_threshold}
                    onChange={(e) =>
                      setCreating((s) => ({ ...s, low_stock_threshold: e.target.value }))
                    }
                    data-testid="shop-create-low-stock"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Online price (RWF)</Label>
                  <Input
                    type="number"
                    value={creating.online_price}
                    onChange={(e) => setCreating((s) => ({ ...s, online_price: e.target.value }))}
                    data-testid="shop-create-online-price"
                  />
                </div>
                <div>
                  <Label>Discount (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="90"
                    value={creating.online_discount_percent}
                    onChange={(e) =>
                      setCreating((s) => ({ ...s, online_discount_percent: e.target.value }))
                    }
                    data-testid="shop-create-online-discount"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={creating.is_visible_online}
                  onCheckedChange={(v) =>
                    setCreating((s) => ({ ...s, is_visible_online: v }))
                  }
                  data-testid="shop-create-visible-switch"
                />
                <Label>Visible on storefront</Label>
              </div>
              <Button onClick={createProduct} className="w-full" data-testid="shop-product-create">
                <Save className="w-4 h-4 mr-1" />
                Create
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
      {/* Sync-from-management dialog */}
      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sync from Management</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Copy management products that aren't yet in the shop. Prices and stock come along —
            you can edit them afterwards without affecting the management inventory.
          </p>
          {syncLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : syncCandidates.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              All management products are already in the shop catalog. Nothing new to sync.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  className="underline text-primary"
                  onClick={toggleSyncAll}
                  data-testid="shop-sync-toggle-all"
                >
                  {syncSelected.size === syncCandidates.length ? 'Deselect all' : 'Select all'}
                </button>
                <span className="text-muted-foreground">
                  {syncSelected.size} of {syncCandidates.length} selected
                </span>
              </div>
              <div className="max-h-96 overflow-y-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-2 text-left w-8"></th>
                      <th className="p-2 text-left">Product</th>
                      <th className="p-2 text-left">Category</th>
                      <th className="p-2 text-right">Base price</th>
                      <th className="p-2 text-right">Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncCandidates.map((c) => (
                      <tr
                        key={c.id}
                        className="border-t hover:bg-muted/20 cursor-pointer"
                        onClick={() => toggleSyncItem(c.id)}
                        data-testid={`shop-sync-row-${c.id}`}
                      >
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={syncSelected.has(c.id)}
                            onChange={() => toggleSyncItem(c.id)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`shop-sync-check-${c.id}`}
                          />
                        </td>
                        <td className="p-2 font-medium">{c.name}</td>
                        <td className="p-2 text-muted-foreground capitalize">{c.category}</td>
                        <td className="p-2 text-right">RWF {formatRWF(c.base_price || 0)}</td>
                        <td className="p-2 text-right">
                          {formatRWF(c.current_stock || 0)} {c.unit}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button
                onClick={runSync}
                className="w-full mt-2"
                data-testid="shop-sync-confirm"
                disabled={syncSelected.size === 0}
              >
                Add {syncSelected.size || ''} to shop
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
